import { keyframes } from 'glamor';
import React, { useEffect } from 'react';
import { ChevronRight } from 'react-feather';
import { Link, useHistory } from 'react-router-dom';

import { EASE_IN_OUT_BACK } from 'styles/easing';
import { createStyles } from 'utils/createStyle';
import { animated, config, useSprings } from 'react-spring';

export interface BreadcrumbProps {
    paths: BreadcrumbLink[];
}

const showItemAnimation = keyframes({
    '0%': {
        transform: 'translateX(-100%)',
        maxWidth: '0%',
        opacity: 0,
    },
    '100%': {
        transform: 'translateX(0%)',
        maxWidth: 'max-content',
        opacity: 1,
    },
});

const hideItemAnimation = keyframes({
    '0%': {
        transform: 'translateX(0%)',
        maxWidth: 'max-content',
        opacity: 1,
    },
    '100%': {
        transform: 'translateX(-100%)',
        maxWidth: '0%',
        opacity: 0,
    },
});

export interface BreadcrumbLink {
    text: string;
    url: string;
    state?: unknown;
    invisible?: boolean;
    disabled?: boolean;
}

const styles = createStyles({
    selected: ['text-brand-dark font-bold'],
    showItem: {
        animationName: showItemAnimation,
        animationFillMode: 'forwards',
        animationDuration: '.5s',
        animationTimingFunction: EASE_IN_OUT_BACK,
    },
    hideItem: {
        animationName: hideItemAnimation,
        animationFillMode: 'forwards',
        animationDuration: '.5s',
        animationTimingFunction: EASE_IN_OUT_BACK,
    },
});

export const Breadcrumb = ({ paths }: BreadcrumbProps) => {
    const history = useHistory();
    const [trail, api] = useSprings(paths.length, (i: number) => ({
        config: config.wobbly,
        from: { x: i * -20, opacity: 0, delay: 100 },
        to: { x: 0, opacity: paths[i].invisible ? 0 : 1, delay: 100 },
    }));

    const visiblePaths = paths.filter(({ invisible }: BreadcrumbLink) => !invisible).length;

    useEffect(() => {
        api.start((i) => ({
            config: config.wobbly,
            from: { x: i * -20, opacity: 0, delay: 100 },
            to: { x: 0, opacity: paths[i].invisible ? 0 : 1, delay: 100 },
        }));
        // eslint-disable-next-line
    }, [visiblePaths]);

    return (
        <nav>
            <ul className="list-reset flex text-xs truncate">
                {trail.map((styles_, i) => {
                    const { state, url, text, disabled, invisible } = paths[i];
                    return (
                        <animated.li
                            key={i}
                            style={{
                                ...styles_,
                                width: 'auto',
                                display: invisible ? 'none' : 'block',
                            }}
                        >
                            <div className="text-brand-accent flex items-center text-left">
                                {disabled && (
                                    <div className={'text-left no-underline text-grey-1'}>
                                        {text}
                                    </div>
                                )}
                                {!disabled && (
                                    <Link
                                        to={{ pathname: url, state }}
                                        className={`text-brand text-left ${
                                            url === history.location.pathname ? styles.selected : ''
                                        }`}
                                    >
                                        {text}
                                    </Link>
                                )}
                                <span
                                    style={{
                                        visibility: i < paths.length - 1 ? 'visible' : 'hidden',
                                    }}
                                >
                                    <ChevronRight width={20} height={20} />
                                </span>
                            </div>
                        </animated.li>
                    );
                })}
            </ul>
        </nav>
    );
};
